import React, { useEffect, useMemo } from 'react';
import {
	Typography,
	TableContainer,
	Table,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	Checkbox,
	Stack,
} from '@mui/material';
import { FolioTableHead } from '@/components/table/FolioTableHead';
import { TableProgressIndicator } from '@/components/table/TableProgressIndicator';
import { useGetClientPoliciesQuery } from '@/features/clients/clientsApi';
import { stableSort } from '@/components/table/functions';
import { getYearFolderRange, policyDataHeadCells, policyHeadCells } from '@/utils/constants';
import { useObjectContext } from '@/hooks/useObjectContext';
import { PolicyRow } from '@/components/tables/PolicyRow';
import {
	useGetDealPoliciesQuery,
	useGetDealPolicyTransactionsQuery,
} from '@/features/deals/dealsApi';
import { PolicyDataRow } from '@/components/tables/PolicyDataRow';
import { usePipelineContext } from '@/context/pipelineContext';
import { usePoliciesSelect } from '@/context/policiesContext';

export const CompleteDealPolicySelection = ({
	editable = false,
	renewalPolicy,
	showMultipleTypes = false,
	pipeline,
	addPolicy = false,
	clientId,
	error = false,
	hideDescription = false,
	displayOnlyCurrentPolicies = false,
	displayOnlyRenewablePolicies = false,
}) => {
	const { selectedPolicies, setSelectedPolicies } = usePoliciesSelect();
	const { dealId, dealRow, clientRow } = useObjectContext();
	const resolvedClientId = clientId ?? clientRow?.clientId;
	const clientInsightId = clientRow?.insightId;

	const dealPoliciesQuery = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });
	const dealPolicies = dealPoliciesQuery?.data?.policies ?? [];

	const { stages } = usePipelineContext(dealRow?.pipeline);
	const currentStage = stages.find((s) => s.id === dealRow?.dealStage);

	const isEndorsementStage = currentStage?.isEndorsement;

	const isEndorsement =
		(dealRow?.pipeline ?? pipeline) === process.env.REACT_APP_PIPELINE_ENDORSEMENTS ||
		isEndorsementStage;
	const isRenewal = (dealRow?.pipeline ?? pipeline) === process.env.REACT_APP_PIPELINE_RENEWALS;

	const policyId = useMemo(() => {
		return renewalPolicy?.Id;
	}, [dealPolicies, renewalPolicy]);

	const {
		data: policyTransactionsData,
		isLoading: isPolicyTransactionsLoading,
		isError: isPolicyTransactionsError,
		isFetching: isPolicyTransactionsFetching,
		isUninitialized: isPolicyTransactionsUninitialized,
	} = useGetDealPolicyTransactionsQuery(
		{
			policyId,
			showMultipleTypes,
			dealId,
			transactionType: isEndorsement ? 'endorsement' : isRenewal ? 'renewal' : 'newBusiness',
		},
		{
			skip: !(editable || isEndorsement || isRenewal) || !policyId,
		},
	);

	const {
		data: clientPoliciesData,
		isLoading: isClientPoliciesLoading,
		isError: isClientPoliciesError,
		isFetching: isClientPoliciesFetching,
		isUninitialized: isClientPoliciesUninitialized,
	} = useGetClientPoliciesQuery(
		{
			insightId: clientInsightId,
			clientId: resolvedClientId,
			transactionType: 'newBusiness',
			dealYearFolder: addPolicy ? dealRow?.yearFolder : null,
			status: displayOnlyCurrentPolicies ? 0 : null,
			typeId: displayOnlyRenewablePolicies ? 0 : null,
			//Status of 0 only recieves current policies
		},
		{
			skip: (!addPolicy && (editable || isEndorsement || isRenewal)) || !resolvedClientId,
		},
	);

	const effectiveData = useMemo(() => {
		if (!addPolicy && (editable || isEndorsement || isRenewal)) {
			return policyTransactionsData;
		}
		return clientPoliciesData;
	}, [editable, isEndorsement, isRenewal, addPolicy, policyTransactionsData, clientPoliciesData]);

	const effectiveLoading = useMemo(() => {
		if (addPolicy) {
			return (
				isClientPoliciesLoading || isClientPoliciesFetching || isClientPoliciesUninitialized
			);
		}
		if (editable || isEndorsement || isRenewal) {
			return (
				isPolicyTransactionsLoading ||
				isPolicyTransactionsFetching ||
				isPolicyTransactionsUninitialized
			);
		}
		return isClientPoliciesLoading || isClientPoliciesFetching || isClientPoliciesUninitialized;
	}, [
		addPolicy,
		editable,
		isEndorsement,
		isRenewal,
		isPolicyTransactionsLoading,
		isPolicyTransactionsFetching,
		isPolicyTransactionsUninitialized,
		isClientPoliciesLoading,
		isClientPoliciesFetching,
		isClientPoliciesUninitialized,
	]);

	const effectiveError = useMemo(() => {
		if (addPolicy) {
			return isClientPoliciesError;
		}
		if (editable || isEndorsement || isRenewal) {
			return (
				isPolicyTransactionsError || (!policyId && (editable || isEndorsement || isRenewal))
			);
		}
		return isClientPoliciesError;
	}, [
		addPolicy,
		editable,
		isEndorsement,
		isRenewal,
		isPolicyTransactionsError,
		isClientPoliciesError,
		policyId,
	]);

	const handleCheckboxChange = (event, policyTransId) => {
		const policy = policyRows.find((p) => p.Id === policyTransId);
		if (!policy) return;

		const policyIdToUse = policy.Id;

		const isSelected = selectedPolicies.some(
			(p) =>
				p.Id === policyIdToUse ||
				p.insightId === policyIdToUse ||
				p.policyId === policyIdToUse,
		);

		let newPolicies;
		if (!isSelected) {
			// Adding policy
			if (addPolicy) {
				newPolicies = [
					...selectedPolicies,
					{
						Id: policyIdToUse,
						insightId: policyIdToUse,
						policyId: policyIdToUse,
						policy: policy,
					},
				];
			} else {
				newPolicies = [
					...selectedPolicies,
					{
						Id: policyIdToUse,
						insightId: policyIdToUse,
						policyId: policyIdToUse,
						policyTransId: policyId
							? policyTransId
							: policy?.policyTransactions?.[0]?.Id,
						policy: policy,
					},
				];
			}
		} else {
			// Removing policy - filter by any of the ID fields
			newPolicies = selectedPolicies.filter(
				(p) =>
					p.Id !== policyIdToUse &&
					p.insightId !== policyIdToUse &&
					p.policyId !== policyIdToUse,
			);
		}

		setSelectedPolicies(newPolicies);
	};

	const handleSelectAll = (event) => {
		const isChecked = event.target.checked;

		if (effectiveData && effectiveData.length > 0) {
			if (isChecked) {
				let newPolicies;
				if (addPolicy) {
					newPolicies = effectiveData
						.filter((policy) => !policy.associated)
						.map((policy) => ({
							Id: policy.Id,
							insightId: policy.Id,
							policyId: policy.Id,
							policy: policy,
						}));
				} else {
					newPolicies = effectiveData
						.filter((policy) => !policy.associated)
						.map((policy) => ({
							Id: policy.Id,
							insightId: policy.Id,
							policyId: policy.Id,
							policyTransId: policyId
								? policy.Id
								: policy?.policyTransactions?.[0]?.Id,
							policy: policy,
						}));
				}
				setSelectedPolicies(newPolicies);
			} else {
				setSelectedPolicies([]);
			}
		}
	};

	const checkIfPolicySelected = (policy) => {
		if (!policy) return false;

		const policyId = policy.Id;
		return selectedPolicies.some(
			(p) => p.Id === policyId || p.insightId === policyId || p.policyId === policyId,
		);
	};

	const selectedPolicyIds = useMemo(() => {
		return selectedPolicies
			.map((policy) => policy.Id ?? policy.insightId ?? policy.policyId)
			.filter((id) => id != null);
	}, [selectedPolicies]);

	const [folderStart, folderEnd] = useMemo(
		() => getYearFolderRange(dealRow?.yearFolder),
		[dealRow?.yearFolder],
	);

	//Filter out policies based on the folder range
	const policyRows = useMemo(() => {
		const updatedPolicies = (effectiveData ?? []).map((policy) => {
			return {
				...policy,
				selected: checkIfPolicySelected(policy),
				linkedToCurrentDeal: editable && dealPolicies.some((p) => p.Id === policy.Id),
			};
		});

		return updatedPolicies;
	}, [
		effectiveData,
		editable,
		dealPolicies,
		selectedPolicyIds,
		// folderStart,
		// folderEnd,
	]);

	const noPolicyId = (editable || isEndorsement || isRenewal) && !policyId && !addPolicy;

	const isError = effectiveError || noPolicyId;
	const loadingState = !isError && effectiveLoading;

	const policyHeaderCell = useMemo(() => {
		if (!policyDataHeadCells.some((cell) => cell.id === 'renewalDate')) {
			policyDataHeadCells.push({
				id: 'renewalDate',
				numeric: false,
				label: 'Renewal date',
				align: 'left',
				sortable: false,
			});
		}

		return addPolicy ? policyDataHeadCells : policyHeadCells;
	}, [addPolicy]);

	return (
		<>
			{!hideDescription && (
				<Stack>
					<Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
						{!addPolicy
							? `Choose the INSIGHT policy transaction that relates to policy: ${
									renewalPolicy?.PolicyNumber ?? dealPolicies?.[0]?.PolicyNumber
								}`
							: 'Choose the INSIGHT policy that relates to this deal'}
					</Typography>
					{!editable && !(isEndorsement || isRenewal) && (
						<Typography variant='subtitle2'>
							{
								'*The selected policy will be linked to the renewal deal at the end of the term.'
							}
						</Typography>
					)}
				</Stack>
			)}

			<TableContainer
				component={Paper}
				sx={{
					borderRadius: '8px',
					overflow: 'hidden',
					boxShadow: 1,
					mt: 1,
					border: error ? '1px solid red' : 'none',
					// borderTop: '1px solid #e0e0e0',
				}}
			>
				<Table
					aria-labelledby='tableTitle'
					size='medium'
					aria-label='enhanced table'
					sx={{
						minWidth: 700,
						'& .MuiTableRow-root:last-child .MuiTableCell-root': {
							borderBottom: 'none',
						},
					}}
				>
					<FolioTableHead
						order={'desc'}
						orderBy={'createDate'}
						headCells={policyHeaderCell}
						headerBackground={true}
						CustomCheckbox={
							<Checkbox
								disabled={policyRows.length === 0}
								onChange={handleSelectAll}
								indeterminate={
									selectedPolicyIds.length > 0 &&
									selectedPolicyIds.length < policyRows.length
								}
								checked={
									selectedPolicyIds.length === policyRows.length &&
									policyRows.length > 0
								}
							/>
						}
						rowCount={policyRows.length}
					/>
					<TableBody>
						{isError ? (
							<TableRow>
								<TableCell colSpan={policyHeaderCell.length} align='center'>
									<Typography variant='subtitle1'>
										{noPolicyId
											? 'No policy transaction found for this policy. '
											: 'There was an error. Please try again.'}
									</Typography>
								</TableCell>
							</TableRow>
						) : resolvedClientId && loadingState ? (
							<TableProgressIndicator rowCount={policyHeaderCell.length} />
						) : !resolvedClientId && loadingState ? (
							<TableRow>
								<TableCell colSpan={policyHeadCells.length} align='center'>
									<Typography variant='subtitle1'>
										{'Select a client to see associated policies.'}
									</Typography>
								</TableCell>
							</TableRow>
						) : policyRows.length > 0 ? (
							stableSort(policyRows, (a, b) => a.Id - b.Id).map((policy, index) =>
								addPolicy ? (
									<PolicyDataRow
										handleCheckboxChange={handleCheckboxChange}
										selected={checkIfPolicySelected(policy)}
										key={`add-policy-row-index-${index}-${policy.Id}`}
										policy={{
											...policy,
											selected: checkIfPolicySelected(policy),
										}}
										showDisabledRow={true}
										showRenewal={addPolicy}
									/>
								) : (
									<PolicyRow
										handleCheckboxChange={handleCheckboxChange}
										selected={checkIfPolicySelected(policy)}
										key={`policy-row-index-${index}-${policy.Id}`}
										policy={{
											...policy,
											selected: checkIfPolicySelected(policy),
										}}
										showDescription={true}
									/>
								),
							)
						) : (
							<TableRow>
								<TableCell colSpan={policyHeadCells.length} align='center'>
									<Typography variant='subtitle1'>
										{
											'No policies found for this client. Please create in INSIGHT first.'
										}
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};
